/* global google */
import React, { PureComponent } from 'react';
import {
  bool,
  arrayOf,
  object,
  number,
  oneOf,
  func,
  string,
  shape,
  node,
  oneOfType,
} from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { show, hide } from 'actions/banner';
import { updateLocation, updateZoom, updateBounds } from 'actions/map';
import { withGoogleMap } from 'react-google-maps/lib/withGoogleMap';
import { withScriptjs } from 'react-google-maps/lib/withScriptjs';
import DrawingManager from 'react-google-maps/lib/components/drawing/DrawingManager';
import Marker from 'react-google-maps/lib/components/Marker';
import GoogleMap from 'react-google-maps/lib/components/GoogleMap';

import { coordEach as turfCoordEach } from '@turf/meta';

import * as constants from 'common/constants';
import { Online } from 'common/components';
import PolygonWrapper from './PolygonWrapper';
import MarkerWithInfoWindow from './MarkerWithInfoWindow';
import CustomControl from './CustomControl';
import LocationButton from '../LocationButton';
import { store } from 'store';

import { coordsToLatLng, sqMetersToAcres } from './util';
import { devLogger } from 'util/devLogger';
import { isDesktop, getHarvestedFlag } from 'common/utils';
import { MAP_PINS, DRAWING_SHAPE_OPTIONS, STYLES } from './constants';
import { OfflineOverlay } from './OfflineOverlay';

import './index.css';

const extendBoundsToPolygon = (bounds, polygon) => {
  if (polygon.geojson) {
    turfCoordEach(polygon.geojson, coords => {
      bounds.extend(coordsToLatLng(coords));
    });
  } else if (polygon.paths) {
    // We presumably have a list of coordinates
    polygon.paths.forEach(latLng => {
      bounds.extend(latLng);
    });
  }
};

/**
 *
 *
 * @class MapComponent
 * @extends {PureComponent}
 *
 */
export class MapComponent extends PureComponent {
  static displayName = 'MapComponent';

  constructor(props) {
    super(props);
    props.showFields && this._extendBoundsWithPolygonsFromProps(props);
    props.showStorages && this._extendBoundsWithMarkersFromProps(props);
    this.mapComponentRef = props.mapRef ? props.mapRef : React.createRef();
  }

  static propTypes = {
    allowMarkerDrop: bool,
    children: node,
    control: oneOfType([
      shape({
        position: string,
        component: node,
      }),
      bool,
    ]),
    clearMarkerFlag: bool,
    crops: arrayOf(object),
    defaultZoom: number,
    disableZoom: bool,
    enableDrawing: bool,
    gestureHandling: oneOf(['none', 'cooperative', 'greedy']),
    hide: func,
    hideZoomControl: bool,
    ignoreMapUpdate: bool,
    ignorePreservedBounds: bool,
    ignorePreservedLocation: bool,
    ignorePreservedZoom: bool,
    initLat: number,
    initLng: number,
    mapRef: object,
    marker: object,
    markerDraggable: bool,
    markerList: arrayOf(object),
    onLocationUpdate: func,
    onPolygonCompleted: func,
    onPolygonUpdated: func,
    pinIcon: oneOf(['field', 'storage']),
    polygons: arrayOf(object),
    selectedField: arrayOf(object),
    show: func,
    showFields: bool,
    showStorageInfo: bool,
    showStorages: bool,
    showStorageMarkers: bool, // toggle whether to show all storage markers or show single marker for editing
    storages: arrayOf(object),
    storage: object,
    storedBounds: object,
    storedLocation: object,
    storedSelectedField: arrayOf(object),
    storedZoom: number,
    type: string,
    updateBounds: func,
    updateLocation: func,
    updateZoom: func,
    usePreservedLocation: bool,
    useMaxZoomService: bool,
    withCurrentLocation: bool,
    withMarker: bool,
    withPolygon: bool,
    withRectangle: bool,
  };

  static defaultProps = {
    allowMarkerDrop: false,
    defaultZoom: 7,
    disableZoom: false,
    enableDrawing: false,
    gestureHandling: 'greedy',
    hideZoomControl: !isDesktop(),
    ignoreMapUpdate: false,
    ignorePreservedBounds: false,
    ignorePreservedLocation: false,
    ignorePreservedZoom: false,
    markerList: [],
    pinIcon: 'field',
    polygons: [],
    showFields: false,
    showStorageInfo: true,
    showStorageMarkers: false,
    showStorages: false,
    storages: [],
    usePreservedLocation: false,
    useMaxZoomService: false,
    withCurrentLocation: true,
    withMarker: false,
    withPolygon: true,
  };

  state = {
    initMap: false,
    polygonsArray: [],
    polygons: [],
    marker: this.props.marker || null,
    geoLat: null,
    geoLng: null,
    geoLocationFetched: null,
    maxZoomServiceDone: false,
    infoWindowFlag: true,
  };

  bounds = new window.google.maps.LatLngBounds();

  componentDidMount() {
    const {
      selectedField,
      storedSelectedField,
      usePreservedLocation,
    } = this.props;
    /**
     * Use max zoom service if we have storages on the page
     */
    // Disable maxZoomService untill we have preserve map location functionality fleshed out
    // this._getZoomForStorages();

    /**
     * Use preserved location, zoom and bounds if allowed
     */
    if (usePreservedLocation) {
      this._usePreservedInformation();
    }

    // Make sure storages show on the storages page
    this._setFitBoundsForStorages();
    this._handleAutoSaveLocation();

    const chosenField = storedSelectedField || selectedField;
    if (chosenField) {
      this._setFitBoundsForPolygon(chosenField);
    }
  }

  componentDidUpdate(prevProps) {
    const { storedSelectedField, clearMarkerFlag } = this.props;

    if (clearMarkerFlag) {
      this.setState({ marker: null }, () =>
        store.dispatch({ type: constants.CLEAR_MARKER, payload: false })
      );
    }

    const comparePolygonList = polygonList =>
      (polygonList || [])
        .map(polygon => polygon.title)
        .sort()
        .join('\n');
    const { showFields, showStorages, polygons } = this.props;
    if (
      showFields !== prevProps.showFields ||
      showStorages !== prevProps.showStorages ||
      comparePolygonList(polygons) !== comparePolygonList(prevProps.polygons)
    ) {
      this.bounds = new window.google.maps.LatLngBounds();
      showFields && this._extendBoundsWithPolygonsFromProps(this.props);
      showStorages && this._extendBoundsWithMarkersFromProps(this.props);
      if (!this.bounds.isEmpty() && (showFields || showStorages)) {
        this.mapComponentRef.current.fitBounds(this.bounds);
      }
    }

    if (
      storedSelectedField &&
      prevProps.storedSelectedField !== storedSelectedField
    ) {
      this._setFitBoundsForPolygon(storedSelectedField);
    }
  }

  render() {
    const {
      children,
      showStorageMarkers,
      showStorageInfo,
      enableDrawing,
    } = this.props;
    return (
      <GoogleMap
        ref={this.mapComponentRef}
        defaultZoom={this._getZoomOnRender()}
        defaultCenter={this._getDefaultCenter()}
        mapTypeId={google.maps.MapTypeId.HYBRID}
        onClick={this._handleMapClick}
        onDragEnd={() => this._handleMapChange({ type: 'dragEnd' })}
        onZoomChanged={() => this._handleMapChange({ type: 'zoomChanged' })}
        options={this._getMapOptions()}
      >
        {this._buildPolygons()}
        {this._buildMarkers()}
        {showStorageMarkers
          ? this._buildStorageMarkers(showStorageInfo)
          : this._getSingleMarker()}
        <Marker position={this._getGeolocationCoordinates()} />
        {enableDrawing && (
          <DrawingManager
            options={this._getDrawingManagerOptions()}
            onPolygonComplete={this._handlePolygonComplete}
          />
        )}
        {this._renderGeolocationButton()}
        {this._renderControl()}
        {children}
      </GoogleMap>
    );
  }

  /**
   *
   * @function _usePreservedInformation
   * @memberof MapComponent
   */
  _usePreservedInformation = () => {
    const { ignoreMapUpdate, storedZoom, storedLocation } = this.props;
    if (!ignoreMapUpdate && storedLocation) {
      setTimeout(() => {
        this._panTo(storedLocation);
        if (storedZoom) {
          this._setZoom(storedZoom);
        }
      }, 0);
    }
  };

  /**
   *
   * @function _extendBoundsWithMarkersFromProps
   * @memberof MapComponent
   * @param {array} - storages
   * @param {array} - markerList
   * @param {boolean} - ignoreMapUpdate
   * @param {boolean} - ignorePreservedBounds
   */
  _extendBoundsWithMarkersFromProps = ({
    storages,
    markerList,
    ignoreMapUpdate,
    ignorePreservedBounds,
  }) => {
    markerList.forEach(marker => this.bounds.extend(marker));
    storages.forEach(storage => {
      if (storage && storage.location) {
        this.bounds.extend(storage.location);
      }
    });

    if (!ignoreMapUpdate && !ignorePreservedBounds && !this.bounds.isEmpty()) {
      this.props.updateBounds(this.bounds);
    }
  };

  /**
   *
   * @function _extendBoundsWithPolygonsFromProps
   * @memberof MapComponent
   * @param {array} - polygons
   * @param {string} - type
   */
  _extendBoundsWithPolygonsFromProps = ({
    polygons,
    type,
    ignoreMapUpdate,
    ignorePreservedBounds,
  }) => {
    if (type === 'editpoly' || !type) {
      (polygons || []).forEach(polygon => {
        extendBoundsToPolygon(this.bounds, polygon);
      });
    } else if (polygons) {
      extendBoundsToPolygon(this.bounds, polygons[polygons.length - 1]);
    }

    if (!ignoreMapUpdate && !ignorePreservedBounds && !this.bounds.isEmpty()) {
      this.props.updateBounds(this.bounds);
    }
  };

  /**
   *
   * @function _buildMarkers
   * @memberof MapComponent
   */
  _buildMarkers = () => {
    return this.props.markerList.map((marker, index) => (
      <Marker
        key={index}
        position={marker}
        icon={{
          url: MAP_PINS['field'],
          anchor: new google.maps.Point(35, 60),
        }}
      />
    ));
  };

  /**
   *
   * @function _buildStorageMarkers
   * @memberof MapComponent
   */
  _buildStorageMarkers = showInfo => {
    const { storages } = this.props;
    const { infoWindowFlag } = this.state;
    const icon = {
      url: MAP_PINS['storage'],
      anchor: new google.maps.Point(35, 60),
    };
    return storages.map((storage, index) =>
      showInfo ? (
        <MarkerWithInfoWindow
          key={index}
          storage={storage}
          icon={icon}
          infoWindowFlag={infoWindowFlag}
          closeAllWindows={this._closeAllWindows}
        />
      ) : (
        <Marker key={index} position={storage.location} icon={icon} />
      )
    );
  };

  /**
   *
   * @function _buildPolygons
   * @memberof MapComponent
   */
  _buildPolygons = () => {
    const { polygons } = this.props;

    const buildPolygonContent = list => {
      let infoList =
        (list || []).length > 0
          ? list.map(
              ({ crop }) => crop.name + (crop.variety ? ` ${crop.variety}` : '')
            )
          : null;
      if ((infoList || []).length > 3) {
        infoList = infoList.slice(0, 3);
        infoList.push('...');
      }
      return infoList;
    };

    return (polygons || []).map(
      (
        {
          geojson,
          paths,
          title,
          location,
          crops,
          disabled = false,
          plantedArea,
          size,
          ...rest
        },
        index
      ) => {
        return (
          <PolygonWrapper
            key={index}
            geojson={geojson}
            paths={paths}
            fieldLocation={location}
            size={size}
            plantedArea={plantedArea}
            harvested={getHarvestedFlag(crops)}
            hasCrops={crops && crops.length > 0}
            disabled={disabled}
            infoTitle={title}
            infoSubTitle={`${size && size.size} ${size && size.type}`}
            infoContent={buildPolygonContent(crops)}
            {...rest}
          />
        );
      }
    );
  };

  /**
   * Getters
   */

  /**
   *
   * @function _getZoomForStorages
   * @memberof MapComponent
   */
  _getZoomForStorages = () => {
    const { ignoreMapUpdate, storages } = this.props;
    if (ignoreMapUpdate && storages.length > 0) this._getMaxZoomForStorages();
  };

  /**
   *
   * @function _getDefaultCenter
   * @memberof MapComponent
   * @returns {object}
   */
  _getDefaultCenter = () => {
    const { initLat = 54.1665653, initLng = -114.6402185, marker } = this.props;

    let location = { lat: initLat, lng: initLng };

    if (marker) {
      location = { lat: marker.lat, lng: marker.lng };
    }

    if (this.bounds && !this.bounds.isEmpty()) {
      location = this.bounds.getCenter();
    }

    return location;
  };

  /**
   *
   * @function _getMaxZoomForStorages
   * @memberof MapComponent
   * Use MaxZoomService to determine a zoom level that will work for the current coordinates
   * This esures we don't fail to load a map in rural areas. Right now this will only be
   * triggered for maps trying to show Storage pins. We can extend more later if needed
   * read more here: https://developers.google.com/maps/documentation/javascript/maxzoom
   */
  _getMaxZoomForStorages = () => {
    const { storages, useMaxZoomService } = this.props;
    if (useMaxZoomService && storages && storages.length) {
      const storagelocation = this.props.storages[0].location;
      const maxZoomService = new google.maps.MaxZoomService();

      maxZoomService.getMaxZoomAtLatLng(storagelocation, result => {
        const { status, zoom } = result;

        if (status === 'OK') {
          devLogger(
            '_getMaxZoomForStorages async call finished, setting zoom to: ',
            zoom,
            'log'
          );
          this._setZoom(zoom);
          this.setState({ maxZoomServiceDone: true });
        }

        if (status !== 'OK') {
          devLogger('_getMaxZoomForStorages not OK', { result }, 'error');
          this._setZoom(18);
          this.setState({ maxZoomServiceDone: true });
        }
      });
    }
  };

  /**
   *
   * @function _getGeolocationCoordinates
   * @memberof MapComponent
   * @returns {(object|null)}
   */
  _getGeolocationCoordinates = () => {
    const { geoLat, geoLng } = this.state;
    return geoLat && geoLng ? { lat: geoLat, lng: geoLng } : null;
  };

  /**
   *
   * @function _getSingleMarker
   * @memberof MapComponent
   */
  _getSingleMarker = () => {
    const { marker, infoWindowFlag } = this.state;
    const { pinIcon, markerDraggable, storage } = this.props;
    let markerComponent = null;

    if (marker && !storage) {
      markerComponent = (
        <Marker
          draggable={markerDraggable}
          onDragEnd={({ latLng }) => {
            const markerLocation = { lat: latLng.lat(), lng: latLng.lng() };
            this.setState({ marker: markerLocation }, () => {
              this.props.onLocationUpdate &&
                this.props.onLocationUpdate(markerLocation);
            });
          }}
          position={marker}
          icon={{
            url: MAP_PINS[pinIcon],
            anchor: new google.maps.Point(35, 60),
          }}
        />
      );
    }

    if (marker && storage) {
      markerComponent = (
        <MarkerWithInfoWindow
          infoWindowFlag={infoWindowFlag}
          closeAllWindows={this._closeAllWindows}
          storage={storage}
          icon={{
            url: MAP_PINS[pinIcon],
            anchor: new google.maps.Point(35, 60),
          }}
          markerProps={{
            draggable: markerDraggable,
            onDragEnd: ({ latLng }) => {
              const markerLocation = { lat: latLng.lat(), lng: latLng.lng() };
              this.setState({ marker: markerLocation }, () => {
                this.props.onLocationUpdate &&
                  this.props.onLocationUpdate(markerLocation);
              });
            },
          }}
        />
      );
    }

    return markerComponent;
  };

  /**
   *
   * @function _getMapOptions
   * @memberof MapComponent
   */
  _getMapOptions = () => {
    const {
      enableDrawing,
      gestureHandling,
      hideZoomControl,
      disableZoom,
    } = this.props;
    return {
      tilt: 0,
      streetViewControl: false,
      mapTypeControl: false,
      draggable: !enableDrawing,
      gestureHandling,
      zoomControl: !hideZoomControl,
      styles: STYLES,
      scrollwheel: !disableZoom,
    };
  };

  /**
   *
   * @function _getDrawingManagerOptions
   * @memberof MapComponent
   */
  _getDrawingManagerOptions = () => {
    const { withMarker, withPolygon } = this.props;

    return {
      drawingControl: isDesktop(),
      drawingMode: 'polygon',
      drawingControlOptions: {
        position: google.maps.ControlPosition.RIGHT_CENTER,
        drawingModes: [
          withMarker && google.maps.drawing.OverlayType.MARKER,
          withPolygon && google.maps.drawing.OverlayType.POLYGON,
        ],
      },
      polygonOptions: DRAWING_SHAPE_OPTIONS,
    };
  };

  /**
   *
   * @function _getZoomOnRender
   * @memberof MapComponent
   */
  _getZoomOnRender = () => {
    const { height, width } = document
      .getElementById('googleMapElement')
      .getBoundingClientRect();
    const { defaultZoom } = this.props;
    let zoom = defaultZoom;

    if (this.props.polygons.length >= 1) {
      zoom = this._getBoundsZoomLevel({ height, width });
    }

    return Math.min(zoom, 16);
  };

  /**
   *
   * @function _getBoundsZoomLevel
   * @memberof MapComponent
   * @param {array}
   */
  _getBoundsZoomLevel = mapDim => {
    // TODO: @Rock ask me why I commented this out
    // this.bounds = new window.google.maps.LatLngBounds();
    const WORLD_DIM = { height: 256, width: 256 };
    const ZOOM_MAX = 21;

    function latRad(lat) {
      const sin = Math.sin((lat * Math.PI) / 180);
      const radX2 = Math.log((1 + sin) / (1 - sin)) / 2;
      return Math.max(Math.min(radX2, Math.PI), -Math.PI) / 2;
    }

    function zoom(mapPx, worldPx, fraction) {
      return Math.floor(Math.log(mapPx / worldPx / fraction) / Math.LN2);
    }

    if (this.bounds.isEmpty()) {
      return 7;
    }

    const ne = this.bounds.getNorthEast();
    const sw = this.bounds.getSouthWest();

    const latFraction = (latRad(ne.lat()) - latRad(sw.lat())) / Math.PI;

    const lngDiff = ne.lng() - sw.lng();
    const lngFraction = (lngDiff < 0 ? lngDiff + 360 : lngDiff) / 360;

    const latZoom = zoom(mapDim.height, WORLD_DIM.height, latFraction);
    const lngZoom = zoom(mapDim.width, WORLD_DIM.width, lngFraction);

    return Math.min(latZoom, lngZoom, ZOOM_MAX);
  };

  /**
   *
   * @function _getPolygonVertexArray
   * @memberof MapComponent
   * @param {object}
   * At this moment, we only take care of Polygon type
   */
  _getPolygonVertexArray = polygon => {
    let polygonsArray = [];
    (polygon.getPath().getArray() || []).forEach(polygonPoint => {
      polygonsArray.push(polygonPoint.toJSON());
    });
    this.setState({ polygonsArray, marker: polygonsArray[0] });

    // Get all polygon points and create a map bounds then fit map into it
    this.bounds = new window.google.maps.LatLngBounds();
    (this.state.polygonsArray || []).forEach(polygonVtx =>
      this.bounds.extend(polygonVtx)
    );
    this.mapComponentRef.current &&
      this.mapComponentRef.current.fitBounds(this.bounds);
    !this.props.ignoreMapUpdate &&
      !this.bounds.isEmpty() &&
      updateBounds(this.bounds);

    const { onPolygonCompleted, onPolygonUpdated } = this.props;
    onPolygonCompleted && onPolygonCompleted();
    onPolygonUpdated &&
      onPolygonUpdated(
        this.state.marker,
        this.state.polygonsArray,
        sqMetersToAcres(
          google.maps.geometry.spherical.computeArea(polygon.getPath())
        )
      );
  };

  /**
   * Setters
   */

  _setFitBoundsForStorages = () => {
    if (!this.bounds.isEmpty() && this.props.storages.length > 1) {
      this.mapComponentRef.current &&
        this.mapComponentRef.current.fitBounds(this.bounds);
    }
  };
  _setZoom = num => {
    this.mapComponentRef.current &&
      this.mapComponentRef.current.context.__SECRET_MAP_DO_NOT_USE_OR_YOU_WILL_BE_FIRED.setZoom(
        num
      );
  };

  _panTo = location => {
    this.mapComponentRef.current &&
      this.mapComponentRef.current.context.__SECRET_MAP_DO_NOT_USE_OR_YOU_WILL_BE_FIRED.panTo(
        location
      );
  };

  // This function is mosting for scouting form
  // When field/polygon is selected, we pan the map to this location
  _setFitBoundsForPolygon = (newPolygon = []) => {
    // First we pan the map to the polygon
    const newBound = new window.google.maps.LatLngBounds();
    newPolygon.forEach(polygonVtx => newBound.extend(polygonVtx));
    this.mapComponentRef.current &&
      this.mapComponentRef.current.fitBounds(newBound);

    // Then we add a maker to the center of the polygon as the scouting point
    const polygonCenter = newBound.getCenter();
    this._handleMapClick({ latLng: polygonCenter });
  };

  /**
   *
   * @function _renderGeolocationButton
   * @memberof MapComponent
   */
  _renderGeolocationButton = () => {
    /**
     * If the browser does not support Geolocation, exit
     */
    if (!navigator || !('geolocation' in navigator)) return null;
    const { withCurrentLocation } = this.props;

    if (this.mapComponentRef.current && withCurrentLocation) {
      return (
        <CustomControl
          controlPosition={window.google.maps.ControlPosition.RIGHT_BOTTOM}
        >
          <LocationButton onClick={this._fetchGeoLocation} />
        </CustomControl>
      );
    }

    return null;
  };

  /**
   *
   * @function _renderControl
   * @memberof MapComponent
   */
  _renderControl = () => {
    const { control } = this.props;
    if (this.mapComponentRef.current && control) {
      const { position, component } = control;
      return (
        <CustomControl
          controlPosition={window.google.maps.ControlPosition[position]}
        >
          {component}
        </CustomControl>
      );
    }

    return null;
  };

  /**
   * Event Handlers
   */

  /**
   *
   * @function _handleZoomChanged
   * @memberof MapComponent
   * @param {number}
   */
  _handleZoomChanged = zoom => {
    const { ignorePreservedZoom, updateZoom } = this.props;
    if (!ignorePreservedZoom && !zoom) {
      // devLogger('_handleZoomChanged', this.mapComponentRef.current.getZoom(), 'log');
      updateZoom(this.mapComponentRef.current.getZoom());
    }

    if (zoom) {
      // devLogger('_handleZoomChanged with zoom param', this.mapComponentRef.current.getZoom(), 'log');
      updateZoom(zoom);
    }
  };

  /**
   *
   * @function _handlePolygonComplete
   * @memberof MapComponent
   * @param {object}
   */
  _handlePolygonComplete = polygon => {
    // const { ignorePreservedBounds, storedBounds, ignoreMapUpdate } = this.props;
    const polygonPaths = polygon.getPath();

    // if (!ignorePreservedBounds && !ignoreMapUpdate && storedBounds && !storedBounds.isEmpty()) {
    //   this.mapComponentRef && this.mapComponentRef.current.fitBounds(storedBounds);
    // } else {
    //   this._getPolygonVertexArray(polygon);
    //   this.bounds = new window.google.maps.LatLngBounds();
    //   this.state.polygonsArray.forEach(polygonVtx => this.bounds.extend(polygonVtx));
    //   this.mapComponentRef.current && this.mapComponentRef.current.fitBounds(this.bounds);
    // }

    // Note (AP-1607):
    // _handlePolygonComplete only gets call when we finish drawing the field polygon on map
    // I am not sure why we needed to check if the preservedBounds or MapUpdate are being ignored
    // Removing the condition for now

    this._getPolygonVertexArray(polygon);
    this.bounds = new window.google.maps.LatLngBounds();
    this.state.polygonsArray.forEach(polygonVtx =>
      this.bounds.extend(polygonVtx)
    );
    this.mapComponentRef.current &&
      this.mapComponentRef.current.fitBounds(this.bounds);

    // Monitor further adjustment on this polygon and obtain new coordinates if polygon has been updated
    google.maps.event.addListener(polygonPaths, 'set_at', () => {
      this._getPolygonVertexArray(polygon);
    });
    google.maps.event.addListener(polygonPaths, 'insert_at', () => {
      this._getPolygonVertexArray(polygon);
    });
    google.maps.event.addListener(polygonPaths, 'remove_at', () => {
      this._getPolygonVertexArray(polygon);
    });
  };

  /**
   *
   * @function _handleMapChange
   * @memberof MapComponent
   * @param {object}
   */
  _handleMapChange = event => {
    const { ignoreMapUpdate } = this.props;

    const wasInteractionEvent =
      event &&
      (event.type === 'click' ||
        event.type === 'dragEnd' ||
        event.type === 'zoomChanged');

    /**
     * If the ignoreMapUpdate flag is false, then proceed with updating redux
     * with the current location and zoom
     */
    if (!ignoreMapUpdate) {
      const location = this.mapComponentRef.current.getCenter();
      // devLogger('_handleMapChange: ignoreMapUpdate is false, updating stored location', { location }, 'log');
      this.props.updateLocation(location);
      this._handleZoomChanged();
    }

    /**
     * If the user tiggers a "click", "dragEnd" or "zoomChanged" event,
     * proceed with updating redux with the current location and zoom.
     *
     * This behaviour is tied how we handle the Storage page:
     *  - Ignore updating the map when user navigated to Storage page
     *  - Resume updating the location if the user clicks, drags or zooms
     *
     * TODO: MaxZoomservice may be running and is asynchonous, may cause a side effect to zoom
     * keep an eye on it
     */
    if (wasInteractionEvent) {
      const location = this.mapComponentRef.current.getCenter();
      // devLogger(`_handleMapChange: A ${event.type} event happened, updating stored location`, { location }, 'log');
      updateLocation(location);
      this._handleZoomChanged();
    }
  };

  /**
   *
   * @function _handleAutoSaveLocation
   * @memberof MapComponent
   */
  _handleAutoSaveLocation = () => {
    setTimeout(() => {
      if (!this.mapComponentRef.current) return null;
      const location = this.mapComponentRef.current.getCenter();
      devLogger(
        'AUTO SAVEING LOCATION',
        { lat: location.lat(), lng: location.lng() },
        'log'
      );
      this.props.updateLocation(location);
      this._handleZoomChanged();
    }, 700);
  };

  /**
   *
   * @function _fetchGeoLocation
   * @memberof MapComponent
   * Fetch the user's current location via Geolocation api
   */
  _fetchGeoLocation = () => {
    if (navigator && 'geolocation' in navigator) {
      /**
       * Show location loading message while geolocation is doing it's thing
       */
      this.props.show(
        constants.BANNER_IDS.APP,
        constants.GEOLOCATION_MESSAGES.LOADING
      );

      /**
       * geolocation is available
       */
      navigator.geolocation.getCurrentPosition(
        this._onGetCurrentLocationSuccess,
        this._onGetCurrentLocationError
      );
    } else {
      /**
       * geolocation IS NOT available
       */
      this.props.show(
        constants.BANNER_IDS.APP,
        constants.GEOLOCATION_MESSAGES.NOT_SUPPORTED
      );
      setTimeout(() => {
        this.props.hide(constants.BANNER_IDS.APP);
      }, 5000);
    }
  };

  /**
   *
   * @function _onGetCurrentLocationSuccess
   * @memberof MapComponent
   * @param {Object}
   * Success function called when Geolocation.watchPosition is successful
   */
  _onGetCurrentLocationSuccess = ({ coords }) => {
    const { latitude, longitude } = coords;
    this.setState(
      { geoLat: latitude, geoLng: longitude, geoLocationFetched: true },
      () => {
        const { geoLat, geoLng, geoLocationFetched } = this.state;
        if (geoLat && geoLng && geoLocationFetched) {
          /**
           * Pan to user's geolocation
           */
          this._panTo({ lat: geoLat, lng: geoLng });

          /**
           * Trigger map change event and update in redux
           */
          this._setZoom(18);
          this._handleZoomChanged(18);
          this._handleMapChange();

          /**
           * Save geolocation to state
           */
          this.setState({ geoLat, geoLng });
        }

        if (geoLat && geoLng) {
          /**
           * Hide banner if location has been fetched
           */
          this.props.hide(constants.BANNER_IDS.APP);
        }
      }
    );
  };

  /**
   *
   * @function _onGetCurrentLocationError
   * @memberof MapComponent
   * @param {Object}
   * Error function called when Geolocation.watchPosition is not successful
   * Error object for code 1 = {code: 1, message: "User denied Geolocation"}
   * Error object for code 2 = {code: 2, message: "Origin does not have permission to use Geolocation service"}
   */
  _onGetCurrentLocationError = ({ code }) => {
    if (code === 2 || code === 1) {
      this.props.show(
        constants.BANNER_IDS.APP,
        constants.GEOLOCATION_MESSAGES.NO_PERMISSION
      );
      this.setState({ geoLat: null, geoLng: null, geoLocationFetched: false });
      setTimeout(() => {
        this.props.hide(constants.BANNER_IDS.APP);
      }, 5000);
    }
  };

  /**
   *
   * @function _closeAllWindows
   * @memberof MapComponent
   * used for _handleMapClick() below and <MarkerWithInfoWindow />
   */
  _closeAllWindows = () => {
    // switch off to close all windows once
    this.setState({ infoWindowFlag: false });
    setTimeout(() => this.setState({ infoWindowFlag: true }), 0);
  };

  /**
   *
   * @function _handleMapClick
   * @memberof MapComponent
   * @param {Object}
   */
  _handleMapClick = ({ latLng }) => {
    if (this.props.allowMarkerDrop) {
      const location = { lat: latLng.lat(), lng: latLng.lng() };
      this.setState({ marker: location });
      this.props.onLocationUpdate && this.props.onLocationUpdate(location);
    }

    // if storage info window is enabled, switch on this once
    if (this.props.showStorageInfo) {
      this._closeAllWindows();
    }

    /**
     *
     * Trigger map change function to get location info on click
     */
    this._handleMapChange({ type: 'click' });
  };
}

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      show,
      hide,
      updateLocation,
      updateZoom,
      updateBounds,
    },
    dispatch
  );

const mapStateToProps = ({ map }) => ({
  storedLocation: map.location,
  storedZoom: map.zoom,
  storedBounds: map.bounds,
  storedSelectedField: map.selectedField,
  selectedStorage: map.selectedStorage,
  clearMarkerFlag: map.clearMarkerFlag,

  // Add custom props for google maps
  googleMapURL: constants.GOOGLE_MAP_API,
  loadingElement: <div style={{ height: '100%' }} />,
  containerElement: <div style={{ height: '100%' }} />,
  mapElement: (
    <div
      id="googleMapElement"
      style={{
        height: '100%',
        width: '100%',
      }}
    />
  ),
});

const ConnectedMapComponent = connect(
  mapStateToProps,
  mapDispatchToProps
)(withScriptjs(withGoogleMap(MapComponent)));

const MapComponentIfOnline = ({ className, ...props }) => (
  <div className={`fieldmap column ${className}`}>
    <OfflineOverlay />

    <Online>
      <ConnectedMapComponent {...props} />
    </Online>
  </div>
);
MapComponentIfOnline.propTypes = {
  className: string,
};
MapComponentIfOnline.defaultProps = {
  className: '',
};
export default MapComponentIfOnline;



// WEBPACK FOOTER //
// ./src/common/components/MapComponent/index.js